import { Link } from 'react-router-dom';
import { dramas } from '@/mocks/dramas';
import { newsList } from '@/mocks/news';
import { categories } from '@/mocks/categories';
import DramaCard from '@/components/feature/DramaCard';
import NewsCard from '@/components/feature/NewsCard';
import HeroCarousel from '@/pages/home/components/HeroCarousel';

export default function Home() {
  const featured = dramas.filter((drama) => drama.featured);
  const latest = [...dramas].sort((a, b) => (a.updateTime < b.updateTime ? 1 : -1));
  const ranking = [...dramas].sort((a, b) => b.heat - a.heat).slice(0, 6);
  const hotNews = newsList.slice(0, 5);

  return (
    <div className="w-full">
      <HeroCarousel dramas={featured} />

      <section className="px-4 md:px-6 lg:px-10 py-10 md:py-14">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">电视剧快速分类</h2>
            <p className="text-sm text-foreground-500 mt-1.5">按题材浏览，快速找到你想看的故事类型</p>
          </div>
          <Link
            to="/category"
            className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-600 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            全部剧集
            <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {categories.map((category) => (
            <Link
              key={category.id}
              to={`/category?genre=${encodeURIComponent(category.name)}`}
              className="group flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 hover:bg-primary-50/40 transition-colors cursor-pointer"
            >
              <span className="w-11 h-11 shrink-0 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center text-xl group-hover:bg-accent-100 group-hover:text-accent-700 transition-colors">
                <i className={category.icon}></i>
              </span>
              <span className="min-w-0">
                <span className="block font-heading text-sm md:text-base font-bold text-foreground-950">{category.name}</span>
                <span className="block text-xs text-foreground-500 mt-1 line-clamp-2 leading-relaxed">{category.description}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-6 lg:px-10 pb-10 md:pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8 lg:gap-10">
          <div>
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">最新剧集剧情速递</h2>
                <p className="text-sm text-foreground-500 mt-1.5">紧跟更新节奏，第一时间了解最新剧情走向</p>
              </div>
              <Link
                to="/category"
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                更多
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
              {latest.slice(0, 6).map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          </div>

          <aside className="flex flex-col gap-8">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-bar-chart-2-line text-accent-500"></i>
                  热播榜
                </h2>
                <span className="text-xs text-foreground-400">实时热度</span>
              </div>
              <ul className="space-y-3">
                {ranking.map((drama, index) => (
                  <li key={drama.id}>
                    <Link to={`/detail/${drama.id}`} className="flex items-center gap-3 group cursor-pointer">
                      <span
                        className={`w-7 h-7 shrink-0 rounded-md flex items-center justify-center text-xs font-bold ${
                          index < 3 ? 'bg-accent-500 text-background-50' : 'bg-background-200 text-foreground-600'
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="w-10 h-14 shrink-0 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={drama.cover}
                          alt={`${drama.title} 封面`}
                          title={`${drama.title} - 热播剧集`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                          {drama.title}
                        </span>
                        <span className="block text-xs text-foreground-500 mt-1 line-clamp-1">{drama.genres.join(' · ')}</span>
                      </span>
                      <span className="text-sm font-bold text-accent-600">{drama.rating.toFixed(1)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-newspaper-line text-primary-500"></i>
                  热门影视资讯
                </h2>
                <Link to="/news" className="text-xs text-primary-600 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap">
                  更多
                </Link>
              </div>
              <ul className="space-y-3.5">
                {hotNews.map((article) => (
                  <li key={article.id}>
                    <Link to={`/news/${article.id}`} className="group flex flex-col gap-1 cursor-pointer">
                      <span className="text-sm text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors leading-relaxed">
                        {article.title}
                      </span>
                      <span className="text-xs text-foreground-400 flex items-center gap-2">
                        <span className="text-accent-600">{article.category}</span>
                        {article.date}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="px-4 md:px-6 lg:px-10 pb-10 md:pb-14">
        <div className="rounded-lg bg-secondary-50 border border-secondary-100 p-6 md:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">关于「上司的老婆」影视剧内容站</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mt-4">
                我们专注于电视剧剧情介绍、角色解析与影视行业动态，用结构化的方式整理每一部好剧的故事脉络。
                无论你是想快速了解剧情梗概，还是想深挖角色关系的细节，都能在这里找到权威、清晰的答案。
              </p>
              <div className="grid grid-cols-3 gap-4 mt-6 max-w-lg">
                <div>
                  <div className="font-heading text-2xl font-black text-primary-600">1200+</div>
                  <div className="text-xs text-foreground-500 mt-1">收录剧集</div>
                </div>
                <div>
                  <div className="font-heading text-2xl font-black text-primary-600">8600+</div>
                  <div className="text-xs text-foreground-500 mt-1">角色档案</div>
                </div>
                <div>
                  <div className="font-heading text-2xl font-black text-primary-600">每日</div>
                  <div className="text-xs text-foreground-500 mt-1">资讯更新</div>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
              <Link
                to="/news"
                className="flex-1 flex items-center justify-center gap-2 px-5 py-4 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-newspaper-line"></i>
                浏览影视资讯
              </Link>
              <Link
                to="/category"
                className="flex-1 flex items-center justify-center gap-2 px-5 py-4 rounded-lg bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-layout-grid-line"></i>
                浏览剧集分类
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}